import { runWithErrorReporting } from "$root/api/error-reporting";
import { getApiGen } from "$root/api/factory";
import { WidgetDownloadTrigger } from "$root/components/DropdownMenuTrigger";
import { IconWalls } from "$root/components/IconWall";
import { DropdownMenu, FormField, Label, StepperInput } from "@mdotm/shadcn/mdotm/components";
import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import InstrumentTagClassificationSelect from "$root/functional-areas/instruments/instrument-classification/InstrumentTagClassificationSelect";
import { createInstrumentList, instrumentsRepository } from "$root/functional-areas/instruments/repository";
import { userClassifications$ } from "$root/functional-areas/instruments/user-classifications";
import { buildCompositionExportConfig } from "$root/functional-areas/instruments/export";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { isAsOfReadable } from "$root/pages/PortfolioDetails/as-of/useAsOfSnapshot";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { useDecimalPlaces } from "$root/functional-areas/user/useDecimalPlaces";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import {
	type PortfolioWidgetData,
	PortfolioQueryWidgetBase,
	WidgetStatus,
	portfolioWidgetMissingDataReason,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { AgGridWithModules } from "$root/third-party-integrations/ag-grid/ag-grid";
import { useMappedQueries } from "$root/third-party-integrations/react-query/hooks";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { type ContextContent, downloadContentDisposition, withContext } from "$root/utils";
import { PortfolioContext } from "$root/widgets-architecture/contexts/portfolio";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import { InvestmentEnhancementExportControllerApiFactory } from "@eu.mdotm.api/sphere";
import type { StylableProps } from "@mdotm/shadcn/legacy/components";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { useQuery } from "@tanstack/react-query";
import type { ColDef } from "ag-grid-community";
import type { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type DecimalsLabelProps = {
	htmlFor: string;
};

const DecimalsLabel = ({ htmlFor }: DecimalsLabelProps): React.JSX.Element => {
	return (
		<Label htmlFor={htmlFor} className="mb-px">
			Decimals
		</Label>
	);
};

const Composition = (props: ContextContent<typeof PortfolioContext>) => {
	const { enhanced, portfolio } = props;
	const { t } = useTranslation();

	useWidgetOptions(
		() => ({
			title: t("COMPOSITION.TITLE"),
			fillHeight: true,
		}),
		[t],
	);

	const queryComposition = useQuery({
		...portfolioRepository.composition.getQueryConfig(props.portfolio.uuid ?? "", {
			enhanced,
			currentlyContainsNestedPortfolios: portfolio?.currentlyContainsNestedPortfolios ?? false,
			flattenNestedPortfolios: true,
			asOf: props.asOf,
		}),
		enabled: isAsOfReadable(props.asOf, props.asOfSnapshot),
	});

	const queryUserInstrumentMap = useQuery({
		...instrumentsRepository.userClassifications.getQueryConfig(
			queryComposition.data?.composition?.map((entry) => entry.ticker).filter((ticker) => ticker != null) ?? [],
		),
		enabled: queryComposition.data != null,
	});

	const queryInvestmentsRichInfo = useQuery({
		...instrumentsRepository.investmentsRichInfo.getQueryConfig(queryComposition.data?.composition ?? []),
		enabled: queryComposition.data != null,
	});

	const query = useMappedQueries(
		([{ composition }, userInstrumentsMap, investmentsRichInfo]): PortfolioWidgetData<
			{ instruments: Instrument[] } | undefined
		> => {
			if (!composition) {
				return {
					data: undefined,
					widgetStatus: portfolioWidgetMissingDataReason(props.portfolio, "Composition"),
				};
			}

			// A composition that arrives holding no instrument has nothing to show.
			if (composition.length === 0) {
				return {
					data: undefined,
					widgetStatus: WidgetStatus.EMPTY,
				};
			}

			return {
				data: {
					instruments: createInstrumentList({
						composition,
						userInstrumentsMap,
						investmentData: investmentsRichInfo,
					}),
				},
				widgetStatus: WidgetStatus.READY,
			};
		},
		[props.portfolio],
		[queryComposition, queryUserInstrumentMap, queryInvestmentsRichInfo],
	);

	const classifications = useAtomValue(userClassifications$);

	const columns = useMemo(
		() =>
			enhanced
				? columnsDefinition.instrumentProposalComposition(classifications, "read")
				: columnsDefinition.instrumentComposition(classifications, "read"),
		[enhanced, classifications],
	);
	return (
		// Every empty state this widget reaches — a composition holding no instrument, and the live summary
		// answering EMPTY for an executing enhancement — is a portfolio still being processed, so the live
		// wall says just that; a pinned date replaces it with the dated one.
		<PortfolioQueryWidgetBase query={query} iconWalls={{ empty: <IconWalls.CalculatingPortfolioWidgetData /> }}>
			{({ instruments }, rq) => (
				<CompositionInner key={rq.fetchStatus} ctx={props} rows={instruments} columns={columns} />
			)}
		</PortfolioQueryWidgetBase>
	);
};

const CompositionInner = (props: {
	ctx: ContextContent<typeof PortfolioContext>;
	rows: Instrument[];
	columns: ColDef<Instrument>[];
}) => {
	const { t } = useTranslation();
	const { ctx, rows, columns } = props;
	const { enhanced, portfolio } = ctx;

	const [agGridEl, setAgGridEl] = useState<AgGridReact<Instrument> | null>(null);
	const [groupByClassificationUuid, setGroupByClassificationUuid] = agExtra.useGroupByClassification(agGridEl);

	const { userDecimalPlacesPreferences, customerDecimalPlacesPreferences, handlePrecisionChange } = useDecimalPlaces();

	// biome-ignore lint/correctness/useExhaustiveDependencies: userDecimalPlacesPreferences is a trigger-only dependency — AG Grid must refresh after the state commit
	useEffect(() => {
		const cols = enhanced ? ["WEIGHT", "PREVIOUS_WEIGHT", "ENHANCED_WEIGHT", "DELTA_WEIGHT"] : ["WEIGHT"];
		agGridEl?.api?.refreshCells({ force: true, columns: cols });
	}, [userDecimalPlacesPreferences, agGridEl, enhanced]);

	useWidgetOptions(
		() => ({
			actionHeader: function Download() {
				return (
					<>
						<FormField label={DecimalsLabel}>
							{(fieldProps) => (
								<StepperInput
									{...fieldProps}
									size="x-small"
									value={userDecimalPlacesPreferences ?? null}
									onChange={handlePrecisionChange}
									max={customerDecimalPlacesPreferences}
									min={2}
								/>
							)}
						</FormField>

						<InstrumentTagClassificationSelect
							value={groupByClassificationUuid ?? null}
							onChange={setGroupByClassificationUuid}
						/>

						<DropdownMenu<StylableProps>
							extraTriggerProps={{ className: "self-end" }}
							Trigger={WidgetDownloadTrigger}
							actions={[
								{
									icon: "xls",
									onClickAsync: () =>
										exportPortfolioComposition("composition", {
											enhanced,
											uuid: portfolio.uuid,
											isReference: !!portfolio?.reference,
											asOf: ctx.asOf,
										}),
									label: portfolio?.reference ? "Reference composition" : "Portfolio composition",
								},
								...(portfolio?.status === "PROPOSAL_READY" && enhanced
									? [
											{
												icon: "xls" as const,
												onClickAsync: () =>
													exportPortfolioComposition("trades", {
														enhanced,
														uuid: portfolio.uuid,
														isReference: !!portfolio?.reference,
														asOf: ctx.asOf,
													}),
												label: t("COMPOSITION.DOWNLOAD_TRADES_TITLE"),
											},
										]
									: []),
							]}
						/>
					</>
				);
			},
			title: t("COMPOSITION.TITLE"),
			fillHeight: true,
		}),
		[
			userDecimalPlacesPreferences,
			customerDecimalPlacesPreferences,
			enhanced,
			groupByClassificationUuid,
			handlePrecisionChange,
			portfolio?.reference,
			portfolio?.status,
			setGroupByClassificationUuid,
			t,
			portfolio.uuid,
			ctx.asOf,
		],
	);

	const persistColProps = agExtra.usePersistColState(
		portfolio.reference
			? persistedColStateKeys["tableV3-reference-composition-read"]
			: persistedColStateKeys["tableV3-investment-composition-read"],
	);
	return (
		<>
			{props.ctx.portfolio && (
				<AgGridWithModules<Instrument>
					{...agExtra.aggFunctions()}
					{...persistColProps}
					innerRef={setAgGridEl}
					columnDefs={columns}
					rowData={rows}
					getRowId={(x) => x.data.tickerName!}
				/>
			)}
		</>
	);
};

async function exportPortfolioComposition(
	downloadTarget: "composition" | "trades",
	opts: {
		enhanced: boolean;
		uuid: string;
		isReference: boolean;
		/** Pinned date (`YYYY-MM-DD`) the export is taken at; a reference has no as-of surface to pin. */
		asOf: string | undefined;
	},
) {
	const { enhanced, uuid, isReference, asOf } = opts;
	// Trades only exist for enhanced proposals and have no smart-workspace data provider yet,
	// so they stay on the legacy trades-only export.
	if (downloadTarget === "trades") {
		await runWithErrorReporting(
			async () => {
				const exportEnhancedApi = getApiGen(InvestmentEnhancementExportControllerApiFactory);
				const response = await exportEnhancedApi.exportEnhancedComposition(uuid, "TRADES_ONLY", {
					responseType: "blob",
				});
				await downloadContentDisposition(response);
			},
			{
				area: "portfolio",
				attemptedOperation: {
					message: "download investment trades",
					payload: JSON.stringify({ downloadTarget, enhanced, uuid }),
				},
			},
		);
		return;
	}

	// The widget always shows the exact composition that mirrors the visible table, so it uses the
	// smart-workspace export driven by the table's persisted column preferences (same column-state
	// key as the table above).
	const config = isReference
		? buildCompositionExportConfig("REFERENCE", {
				investmentUuid: uuid,
				snapshot: enhanced ? "PROPOSAL" : "CURRENT",
				colStateKey: persistedColStateKeys["tableV3-reference-composition-read"],
			})
		: buildCompositionExportConfig("INVESTMENT", {
				investmentUuid: uuid,
				snapshot: enhanced ? "PROPOSAL" : "CURRENT",
				colStateKey: enhanced
					? persistedColStateKeys["tableV3-investment-proposalComposition-read"]
					: persistedColStateKeys["tableV3-investment-composition-read"],
				compositionType: "STANDARD",
				asOf,
			});
	await runWithErrorReporting(
		async () => {
			const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
			await downloadContentDisposition(file);
		},
		{
			area: "portfolio",
			attemptedOperation: {
				message: "export investment composition",
				payload: JSON.stringify(config),
			},
		},
	);
}

export default withContext(PortfolioContext)(Composition);
